/**
 * カスタム属性。実体は **Keycloak のユーザープロファイル**(UPConfig)で、
 * ここで宣言した名前だけがユーザーの画面と CSV(`attr:<名前>`)で使える。
 *
 * 値が入っているユーザーが居るうちは消せない(押し切るときだけ `force`)。
 */
import { useMutation } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";

import type { CustomAttr } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useIdpCustomAttrs, useIdpPerms, useInvalidateIdp } from "./hooks";
import { ATTR_NAME_HINT, CUSTOM_ATTR_MAX } from "./userOptions";

const { Text, Paragraph } = Typography;

export function CustomAttributesPage() {
  const api = useApi();
  const invalidate = useInvalidateIdp();
  const { canAdmin } = useIdpPerms();
  const query = useIdpCustomAttrs();
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<{ name: string; displayName: string }>();

  const create = useMutation({
    mutationFn: (v: { name: string; displayName: string }) => api.createIdpCustomAttr(v),
    onSuccess: (res) => {
      invalidate();
      void message.success(`${res.displayName} を追加した`);
      setAdding(false);
      form.resetFields();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (v: { name: string; force: boolean }) =>
      api.deleteIdpCustomAttr(v.name, v.force),
    onSuccess: (res) => {
      invalidate();
      void message.success(`${res.name} を削除した`);
    },
    onError: (e) => void message.error(errorText(e)),
  });

  /** 使われているときは 409 が返る。何人使っているかを見せて押し切らせる。 */
  const askDelete = (name: string) => {
    Modal.confirm({
      title: `${name} を削除する`,
      content: "この属性の値もユーザーから消える(戻せない)。",
      okText: "削除する",
      okButtonProps: { danger: true },
      cancelText: "やめる",
      onOk: async () => {
        try {
          await api.deleteIdpCustomAttr(name, false);
          invalidate();
          void message.success(`${name} を削除した`);
        } catch (e) {
          const text = errorText(e);
          Modal.confirm({
            title: "値が入っているユーザーが居る",
            content: `${text} それでも消すと、その値も一緒に消える。`,
            okText: "それでも削除する",
            okButtonProps: { danger: true },
            cancelText: "やめる",
            onOk: () => remove.mutate({ name, force: true }),
          });
        }
      },
    });
  };

  const items = query.data?.items ?? [];
  const full = items.length >= (query.data?.max ?? CUSTOM_ATTR_MAX);

  const columns: ColumnsType<CustomAttr> = [
    { title: "属性名(CSV の列は attr:<名前>)", width: 340, dataIndex: "name", render: (v: string) => <Text code>{v}</Text> },
    { title: "表示名", width: 160, dataIndex: "displayName" },
    {
      title: "編集できる人",
      dataIndex: "permissions",
      width: 320,
      render: (p: CustomAttr["permissions"]) => (p.edit ?? []).map((r) => <Tag key={r}>{r}</Tag>),
    },
    {
      title: "",
      key: "op",
      width: 100,
      render: (_: unknown, row) =>
        canAdmin && (
          <Button size="small" danger onClick={() => askDelete(row.name)}>
            削除
          </Button>
        ),
    },
  ];

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          カスタム属性
        </Typography.Title>
        {canAdmin && (
          <Button type="primary" disabled={full} onClick={() => setAdding(true)}>
            属性の追加
          </Button>
        )}
      </Flex>

      <Paragraph type="secondary">
        ユーザーに持たせる項目を増やす(部署・社員番号など)。
        {query.data?.max ?? CUSTOM_ATTR_MAX} 個まで。ここで宣言していない名前は、
        ユーザーの追加でも CSV でも受け付けない。
      </Paragraph>

      {full && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`カスタム属性は ${query.data?.max ?? CUSTOM_ATTR_MAX} 個まで`}
          description="要らないものを消してから追加する。"
        />
      )}

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="カスタム属性を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small">
        <Table<CustomAttr>
          scroll={{ x: "max-content" }}
          rowKey="name"
          size="small"
          loading={query.isFetching || remove.isPending}
          columns={columns}
          dataSource={items}
          pagination={false}
          locale={{ emptyText: "カスタム属性はまだ無い" }}
        />
      </Card>

      <Modal
        open={adding}
        title="カスタム属性の追加"
        onCancel={() => setAdding(false)}
        onOk={() => void form.submit()}
        okText="追加する"
        cancelText="やめる"
        confirmLoading={create.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Form.Item
              name="name"
              label="属性名(半角。あとから変えられない)"
              extra={ATTR_NAME_HINT}
              rules={[{ required: true, message: "属性名は必須" }]}
            >
              <Input maxLength={63} placeholder="department" />
            </Form.Item>
            <Form.Item
              name="displayName"
              label="表示名"
              rules={[{ required: true, message: "表示名は必須" }]}
            >
              <Input maxLength={200} placeholder="部署" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
